import { ApiError } from "./api";

/**
 * Error types for specific handling
 */
export type ErrorType =
  | "conflict"      // 409 - seat conflict, resource conflict
  | "route-changed" // 409 - the trip / route / segment changed under the request (retryable)
  | "rate-limit"    // 429 - too many requests
  | "session"       // Session expired
  | "network"       // Network/connection error
  | "payment"       // Payment-related error
  | "validation"    // Form validation errors
  | "not-found"     // 404 - resource not found
  | "server"        // 500+ - server error
  | "unknown";      // Unknown error

/**
 * Parsed error result with type and user-friendly message
 */
export interface ParsedError {
  type: ErrorType;
  message: string;
  title?: string;
  retryAfter?: number; // seconds until retry allowed (for rate-limit)
  fieldErrors?: Record<string, string>; // for validation errors
  originalError?: Error;
}

/**
 * Check if error is a network/connection error
 */
function isNetworkError(error: unknown): boolean {
  if (error instanceof TypeError && error.message === "Failed to fetch") {
    return true;
  }
  if (error instanceof DOMException && error.name === "AbortError") {
    return true;
  }
  return false;
}

/**
 * Parse validation errors from API response
 */
function parseValidationErrors(data: unknown): Record<string, string> | undefined {
  if (!data || typeof data !== "object") return undefined;

  const response = data as Record<string, unknown>;

  // Handle Zod-style errors: { errors: [{ path: ["field"], message: "..." }] }
  if (Array.isArray(response.errors)) {
    const fieldErrors: Record<string, string> = {};
    for (const err of response.errors) {
      if (err && typeof err === "object" && "path" in err && "message" in err) {
        const path = Array.isArray(err.path) ? err.path.join(".") : String(err.path);
        fieldErrors[path] = String(err.message);
      }
    }
    return Object.keys(fieldErrors).length > 0 ? fieldErrors : undefined;
  }

  // Handle flat field errors: { fieldName: "error message" }
  if (response.fieldErrors && typeof response.fieldErrors === "object") {
    return response.fieldErrors as Record<string, string>;
  }

  return undefined;
}

/**
 * Extract retry-after value from API response or headers
 */
function parseRetryAfter(data: unknown): number | undefined {
  if (!data || typeof data !== "object") return undefined;
  const response = data as Record<string, unknown>;
  if (typeof response.retryAfter === "number") {
    return response.retryAfter;
  }
  return undefined;
}

/**
 * 409 codes that mean "the route, trip or segment changed while the request
 * waited" — NOT "someone took your seat". Picking another seat cannot fix
 * these; the customer has to refresh the trip data and re-select.
 */
const ROUTE_CHANGED_CODES = new Set([
  "trip_route_changed",
  "segment_changed",
  "route_structure_changed",
  "route_lock_order",
]);

function conflictCode(data: unknown): string | undefined {
  if (data && typeof data === "object" && "code" in data) {
    const code = (data as { code?: unknown }).code;
    return typeof code === "string" ? code : undefined;
  }
  return undefined;
}

/**
 * Parse an error and return a user-friendly result
 */
export function parseError(error: unknown): ParsedError {
  // Handle network errors
  if (isNetworkError(error)) {
    return {
      type: "network",
      title: "Connection Error",
      message: "Unable to connect. Please check your internet connection and try again.",
      originalError: error instanceof Error ? error : undefined,
    };
  }

  // Handle API errors
  if (error instanceof ApiError) {
    const { status, message, data } = error;

    // 400 - Bad Request (often validation)
    if (status === 400) {
      const fieldErrors = parseValidationErrors(data);
      if (fieldErrors) {
        return {
          type: "validation",
          title: "Validation Error",
          message: message || "Please check your input and try again.",
          fieldErrors,
          originalError: error,
        };
      }
      return {
        type: "unknown",
        title: "Invalid Request",
        message: message || "The request was invalid. Please try again.",
        originalError: error,
      };
    }

    // 404 - Not Found
    if (status === 404) {
      return {
        type: "not-found",
        title: "Not Found",
        message: message || "The requested resource was not found.",
        originalError: error,
      };
    }

    // 409 - route / trip / segment changed under the request (retryable)
    if (status === 409 && ROUTE_CHANGED_CODES.has(conflictCode(data) ?? "")) {
      return {
        type: "route-changed",
        title: "Trip Details Changed",
        message:
          message ||
          "This trip's details changed while you were booking. Please refresh and select your seats again.",
        originalError: error,
      };
    }

    // 409 - Conflict (seat no longer available, etc.)
    if (status === 409) {
      return {
        type: "conflict",
        title: "Conflict",
        message: message || "This resource is no longer available. Please refresh and try again.",
        originalError: error,
      };
    }

    // 429 - Rate Limited
    if (status === 429) {
      const retryAfter = parseRetryAfter(data) || 60; // default 60 seconds
      return {
        type: "rate-limit",
        title: "Too Many Requests",
        message: "Please slow down. You've made too many requests.",
        retryAfter,
        originalError: error,
      };
    }

    // 401/403 - Auth errors (session expired for public booking)
    if (status === 401 || status === 403) {
      return {
        type: "session",
        title: "Session Expired",
        message: "Your session has expired. Please start a new booking.",
        originalError: error,
      };
    }

    // 500+ - Server errors
    if (status >= 500) {
      return {
        type: "server",
        title: "Server Error",
        message: "Something went wrong on our end. Please try again later.",
        originalError: error,
      };
    }

    // Unknown status
    return {
      type: "unknown",
      message: message || "An unexpected error occurred.",
      originalError: error,
    };
  }

  // Handle regular Error objects
  if (error instanceof Error) {
    return {
      type: "unknown",
      message: error.message || "An unexpected error occurred.",
      originalError: error,
    };
  }

  // Handle unknown errors
  return {
    type: "unknown",
    message: "An unexpected error occurred.",
  };
}

/**
 * Get user-friendly message for seat conflict errors
 */
export function getSeatConflictMessage(): string {
  return "This seat is no longer available. It may have been taken by another customer. Please select a different seat.";
}

/**
 * Get user-friendly message for session expiry
 */
export function getSessionExpiredMessage(): string {
  return "Your seat reservation has expired. Please start your booking again.";
}

/**
 * Get user-friendly message for payment errors
 */
export function getPaymentErrorMessage(error?: string): string {
  if (error) {
    // Common Stripe error messages that can be shown as-is
    const safeMessages = [
      "Your card was declined",
      "Your card has insufficient funds",
      "Your card's security code is incorrect",
      "Your card number is incorrect",
      "Your card has expired",
    ];
    if (safeMessages.some((msg) => error.toLowerCase().includes(msg.toLowerCase()))) {
      return error;
    }
  }
  return "Your payment could not be processed. Please check your card details and try again.";
}

/**
 * Check if an error indicates a seat conflict
 */
export function isSeatConflictError(error: unknown): boolean {
  if (error instanceof ApiError) {
    return (
      error.status === 409 && !ROUTE_CHANGED_CODES.has(conflictCode(error.data) ?? "")
    );
  }
  const parsed = parseError(error);
  return parsed.type === "conflict";
}

/**
 * Check if an error is a retryable "the route / trip changed" 409 (not a seat
 * conflict): refresh the trip and select again.
 */
export function isRouteChangedError(error: unknown): boolean {
  return (
    error instanceof ApiError &&
    error.status === 409 &&
    ROUTE_CHANGED_CODES.has(conflictCode(error.data) ?? "")
  );
}

/**
 * Check if an error indicates session expiry
 */
export function isSessionExpiredError(error: unknown): boolean {
  if (error instanceof ApiError) {
    return error.status === 401 || error.status === 403;
  }
  const parsed = parseError(error);
  return parsed.type === "session";
}

/**
 * Check if an error is rate limiting
 */
export function isRateLimitError(error: unknown): boolean {
  if (error instanceof ApiError) {
    return error.status === 429;
  }
  const parsed = parseError(error);
  return parsed.type === "rate-limit";
}

/**
 * Check if an error is a network/connection error.
 * Note: For better performance when checking multiple error types,
 * prefer calling parseError() once and checking .type === "network"
 */
export function checkNetworkError(error: unknown): boolean {
  return parseError(error).type === "network";
}
